"use client";

import SiteLinkNavPaint from "./SiteLinkNavPaint";

export interface SiteLinkGroup { label: string; links: { href: string; text: string }[] }

// Render the selected plain-data snapshot synchronously in SSR and on the client.
// Lazy server-rendered anchor records can suspend after React has claimed their parent
// and retry at an advanced hydration cursor. No fallback: no-JS retains every anchor.
// W2a — EVERY RULE IS DOUBLED ONTO `[data-site-link-nav-paint]`, and it is not tidiness.
// `SiteLinkNavPaint` clones this node into the design's own scroll container and STRIPS
// `data-site-link-nav` from the copy (so an instrument can tell the served band from the painted
// one). Without the second selector the copy would land inside the design carrying none of the
// `!important` floor this block exists to be — i.e. it would be the one copy a visitor actually
// looks at and the only one a captured stylesheet could collapse. The floor belongs to whichever
// copy is on screen, so it names both.
const CSS = `
[data-site-link-nav],[data-site-link-nav-paint]{display:block!important;visibility:visible!important;opacity:1!important;
position:static!important;clip:auto!important;clip-path:none!important;overflow:visible!important;
width:auto!important;height:auto!important;max-height:none!important;text-indent:0!important;
transform:none!important;font-size:13px!important;line-height:1.4!important}
[data-site-link-nav] a,[data-site-link-nav-paint] a{text-decoration:none}
[data-site-link-nav] a:hover,[data-site-link-nav] a:focus-visible,
[data-site-link-nav-paint] a:hover,[data-site-link-nav-paint] a:focus-visible{text-decoration:underline}

/* QA Batch 1 item 11 - the design footer hangs 24px off the right edge at 390.
   MEASURED: the footer computes width 414px inside a main whose content box is 390. No rule sets a
   width; the cause is margin 0 -24px 0 0 from the captured CSSOM, and a block with width auto and a
   negative right margin resolves to its containing block plus 24. main is overflow-x hidden, so the
   excess was CLIPPED rather than scrolled, which is why the document reported no horizontal
   overflow while the footer was visibly cut off.
   AND IT CANNOT BE FIXED WITH AN INLINE STYLE ON THE EXPORT, which is what the first attempt did.
   Measured on deployed 774ae0f9: the design runtime RE-SERIALISES the style attribute when it
   renders the block. role and data-screen-label survived; the width declarations came back as
   style="background: rgb(255, 255, 255);" - dropped silently, so the patch looked applied in the
   served asset and did nothing on screen. The rule therefore lives in a STYLESHEET, which the
   runtime does not touch, and in THIS one because it is already server-rendered on all 400 graded
   paths and is already the block whose job is declarations a captured stylesheet must not beat. */
footer[data-screen-label="Landing — footer"]{width:100%!important;max-width:100%!important;margin-right:0!important}
`;

export default function SiteLinkNavBody({ setId, groups }: { setId: string; groups: SiteLinkGroup[] }) {
  return (
    <>
    <nav
      aria-label="Site navigation"
      data-site-link-nav=""
      data-site-link-set={setId}
      style={{
        display: "block",
        visibility: "visible",
        opacity: 1,
        position: "static",
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "100%",
        background: "var(--sg-grey-50, #f7f8f9)",
        borderTop: "1px solid var(--sg-grey-100, #eceff1)",
        padding: "28px 32px 40px",
        fontFamily: "var(--sg-font-secondary, system-ui)",
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div
        style={{
          boxSizing: "border-box",
          maxWidth: 1240,
          margin: "0 auto",
          // FOUNDER VISUAL ACCEPTANCE 2026-09-02 - "follows content, no excess blank".
          // MEASURED on staging and reproduced locally: as a wrap-flex each group was only as wide
          // as its own text, so four groups of wildly different lengths clumped into the left third
          // and the right half of the band was empty for ~560px while MOUNTAINS ran 30 rows deep.
          // A grid gives every group the same track width the design footer above already uses, so
          // the two blocks read as one footer instead of two. auto-fit collapses the tracks a set
          // does not fill, so a path with two groups still spans the width rather than leaving gaps.
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          columnGap: 48,
          rowGap: 28,
          alignItems: "flex-start",
        }}
      >
        {groups.map((group) => (
          <div
            key={group.label}
            data-site-link-group={group.label}
            style={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              minWidth: 150,
              maxWidth: "100%",
            }}
          >
            <div
              style={{
                // "consistent categories": the design footer's own group headings are dark and bold.
                // These were grey and light, which is what made the band read as a second, lesser
                // footer stapled underneath rather than the same footer continuing.
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1.3,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: "var(--sg-ink-900, #22282d)",
              }}
            >
              {group.label}
            </div>
            {/* A long group flows into two columns inside its own track rather than running as one
                30-row spike beside three near-empty neighbours - the other half of "no excess blank".
                The threshold is on the count, not on a hard-coded group name, so a regenerated set
                gets the same treatment without this file knowing which label grew. */}
            <div style={group.links.length > 12
              ? { columnCount: 2, columnGap: 24, maxWidth: "100%" }
              : { display: "flex", flexDirection: "column", gap: 8, maxWidth: "100%" }}>
              {group.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  style={{
                    display: "block",
                    fontSize: 13,
                    lineHeight: 1.4,
                    color: "var(--sg-grey-600, #5f6b76)",
                    textDecoration: "none",
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                  }}
                >
                  {link.text}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </nav>
    {/* W2a — THE SAME BAND, WHERE THE VISITOR IS LOOKING. On the 370 contract paths that mount the
        founder's design, `ExportShell` and `DesignSurface` each render a `position:fixed; inset:0`
        opaque sheet, so the document body is ~0px tall and NOTHING in normal flow paints — the <nav>
        above is served, correct, crawlable and invisible. This clones it into the design's own
        scroll container after the design's own footer, then hides the copy above. Client-only, so
        the delivered HTML is unchanged; a no-JS visitor and every crawler keep exactly what the
        server sent. On a route with no design (the ContentPage branch) it does nothing at all and
        the <nav> above stays painted in flow. See SiteLinkNavPaint.tsx for the measurements. */}
    <SiteLinkNavPaint setId={setId} />
    </>
  );
}
